import React, { useState, useEffect } from 'react';
import Hero from '../components/Hero';
import VideoCard from '../components/VideoCard';
import EndlessRail from '../components/EndlessRail';
import CrowdfundCard from '../components/CrowdfundCard';
import RailHeader from '../components/home/RailHeader';
import GenreFilter from '../components/home/GenreFilter';
import TrendingTop10Rail from '../components/home/TrendingTop10Rail';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { SkeletonLoader } from '../components/SkeletonLoader';
import { useNavigate } from 'react-router-dom';
import { contentService } from '../services/contentService.js';
import { useAuth } from '../hooks/useAuth.js';
import { useSlicePot } from '../hooks/useSlicePot.js';
import CommunityPickBanner from '../components/pot/CommunityPickBanner';

const RailSection = ({ title, actionLabel, onAction, children, loading, skeleton }) => (
  <section>
    <RailHeader title={title} actionLabel={actionLabel} onAction={onAction} />
    {loading ? skeleton : children}
  </section>
);

const Home = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { worksSummary, isLoading: potLoading } = useSlicePot();
  const [activeSection, setActiveSection] = useState('all');
  const [recentlyUpdated, setRecentlyUpdated] = useState([]);
  const [continueWatchingVideos, setContinueWatchingVideos] = useState([]);
  const [trendingNow, setTrendingNow] = useState([]);
  const [featuredSeries, setFeaturedSeries] = useState([]);
  const [crowdfundingProjects, setCrowdfundingProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);

        const [recentContent, continueWatching, trending, series, campaigns] =
          await Promise.all([
            contentService.getRecentContent({
              limit: 20,
              genre: activeSection === 'all' ? null : activeSection,
            }),
            user ? contentService.getContinueWatching(user.id, 10) : [],
            contentService.getTrendingContent(10),
            contentService.getAllSeries({ limit: 12 }),
            contentService.getCrowdfundingProjects({
              limit: 6,
              status: 'active',
            }),
          ]);

        setRecentlyUpdated(recentContent);
        setContinueWatchingVideos(continueWatching);
        setTrendingNow(trending);
        setFeaturedSeries(series);
        setCrowdfundingProjects(campaigns);
      } catch (err) {
        console.error('Error loading home data:', err);
        setError('Failed to load content. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [user, activeSection]);

  const categories = [
    { id: 'all', name: 'All', count: recentlyUpdated.length },
    {
      id: 'action',
      name: 'Action',
      count: recentlyUpdated.filter((v) => v.tags?.includes('Action')).length,
    },
    {
      id: 'comedy',
      name: 'Comedy',
      count: recentlyUpdated.filter((v) => v.tags?.includes('Comedy')).length,
    },
    {
      id: 'romance',
      name: 'Romance',
      count: recentlyUpdated.filter((v) => v.tags?.includes('Romance')).length,
    },
  ];

  if (error) {
    return (
      <div className="min-h-screen bg-background">
        <Hero />
        <div className="px-4 py-12 md:px-8">
          <Card className="border-border/40 bg-card/40 p-10 text-center">
            <CardContent className="space-y-4">
              <h3 className="text-xl font-semibold">Something went wrong</h3>
              <p className="text-muted-foreground">{error}</p>
              <Button onClick={() => window.location.reload()}>Try Again</Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  const posterSkeleton = (
    <div className="flex gap-3 overflow-hidden">
      {Array.from({ length: 5 }).map((_, i) => (
        <SkeletonLoader
          key={i}
          variant="video"
          className="aspect-[2/3] w-[28%] shrink-0 rounded-lg sm:w-[18%]"
        />
      ))}
    </div>
  );

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Hero />

      <div className="space-y-10 px-4 py-8 md:space-y-14 md:px-8 lg:px-10 md:py-10">
        <GenreFilter
          categories={categories}
          active={activeSection}
          onChange={setActiveSection}
        />

        <CommunityPickBanner worksSummary={worksSummary} isLoading={potLoading} />

        {user && (loading || continueWatchingVideos.length > 0) && (
          <RailSection
            title="Continue Watching"
            loading={loading}
            skeleton={
              <div className="flex gap-3">
                {Array.from({ length: 3 }).map((_, i) => (
                  <SkeletonLoader
                    key={i}
                    variant="video"
                    className="aspect-video w-[70%] shrink-0 rounded-lg sm:w-[40%]"
                  />
                ))}
              </div>
            }
          >
            <EndlessRail
              items={continueWatchingVideos}
              loop={false}
              slideWidth="continue"
              ariaLabel="Continue watching"
              renderItem={(video) => (
                <VideoCard
                  key={`continue-${video.id}`}
                  video={video}
                  variant="continue"
                />
              )}
            />
          </RailSection>
        )}

        <TrendingTop10Rail items={trendingNow} loading={loading} />

        <RailSection
          title="New Releases"
          actionLabel="See All"
          onAction={() => navigate('/browse')}
          loading={loading}
          skeleton={posterSkeleton}
        >
          {recentlyUpdated.length > 0 ? (
            <EndlessRail
              items={recentlyUpdated}
              loop={false}
              slideWidth="poster"
              ariaLabel="New releases"
              renderItem={(video) => (
                <VideoCard key={video.id} video={video} variant="poster" />
              )}
            />
          ) : (
            !loading && (
              <p className="py-8 text-center text-sm text-muted-foreground">
                No titles in this category yet.
              </p>
            )
          )}
        </RailSection>

        {(loading || crowdfundingProjects.length > 0) && (
          <RailSection
            title="Support New Projects"
            actionLabel="See All"
            onAction={() => navigate('/crowdfund')}
            loading={loading}
            skeleton={posterSkeleton}
          >
            <EndlessRail
              items={crowdfundingProjects}
              loop={false}
              max={6}
              slideWidth="poster"
              ariaLabel="Crowdfunding projects"
              renderItem={(project) => (
                <CrowdfundCard key={project.id} project={project} />
              )}
            />
          </RailSection>
        )}

        {(loading || featuredSeries.length > 0) && (
          <RailSection
            title="Kinora Originals"
            actionLabel="See All"
            onAction={() => navigate('/browse?type=series')}
            loading={loading}
            skeleton={posterSkeleton}
          >
            <EndlessRail
              items={featuredSeries}
              loop={false}
              slideWidth="poster"
              ariaLabel="Kinora originals"
              renderItem={(series) => (
                <VideoCard
                  key={series.id}
                  video={{ ...series, type: 'tv_show' }}
                  variant="poster"
                />
              )}
            />
          </RailSection>
        )}
      </div>
    </div>
  );
};

export default Home;
